import { useState, useEffect } from 'react';
import api from '../../../api/client';

const EMPTY_FORM = { supplierId: '', supplierName: '', validUntil: '' };

export default function QualificationsPage() {
  const [qualifications, setQualifications] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [addOpen,        setAddOpen]        = useState(false);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [saving,         setSaving]         = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/sourcing/qualifications');
      setQualifications(data.data?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function patch(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post('/sourcing/qualifications', {
        supplierId:   form.supplierId,
        supplierName: form.supplierName,
        validUntil:   form.validUntil,
      });
      setForm(EMPTY_FORM);
      setAddOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleQualify(id) {
    setError(null);
    try {
      await api.post(`/sourcing/qualifications/${id}/qualify`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  async function handleDisqualify(id) {
    setError(null);
    try {
      await api.post(`/sourcing/qualifications/${id}/disqualify`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  }

  return (
    <div data-testid="vms-qualifications-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Supplier Qualifications</h2>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <div style={{ marginBottom: '1rem' }}>
        <button data-testid="vms-qualification-add" onClick={() => setAddOpen(true)}>
          Add Qualification
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Status</th>
              <th>Valid Until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {qualifications.map((q) => (
              <tr key={q.id} data-testid={`vms-qualification-row-${q.id}`}>
                <td>{q.supplier_name || q.supplier_id}</td>
                <td>{q.status}</td>
                <td>{q.valid_until?.slice(0, 10)}</td>
                <td style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    data-testid={`vms-qualify-${q.id}`}
                    onClick={() => handleQualify(q.id)}
                  >
                    Qualify
                  </button>
                  <button
                    data-testid={`vms-disqualify-${q.id}`}
                    onClick={() => handleDisqualify(q.id)}
                  >
                    Disqualify
                  </button>
                </td>
              </tr>
            ))}
            {!qualifications.length && <tr><td colSpan={4}>No qualifications yet.</td></tr>}
          </tbody>
        </table>
      )}

      {addOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.5rem', minWidth: 360 }}>
            <h3>Add Qualification</h3>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  placeholder="Supplier ID"
                  value={form.supplierId}
                  onChange={(e) => patch('supplierId', e.target.value)}
                  required
                />
                <input
                  placeholder="Supplier Name"
                  value={form.supplierName}
                  onChange={(e) => patch('supplierName', e.target.value)}
                  required
                />
                <label>
                  Valid Until
                  <input
                    type="date"
                    value={form.validUntil}
                    onChange={(e) => patch('validUntil', e.target.value)}
                  />
                </label>
              </div>
              {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" data-testid="vms-qualification-form-submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={() => { setAddOpen(false); setError(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
